import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useT } from "@/context/LangContext";
import { useBrand } from "@/context/BrandContext";

export default function Header() {
  const [scrolled, setScrolled] = React.useState(false);
  const loc = useLocation();
  const { t } = useT();
  const { brand } = useBrand();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { to: "/#partenaires", label: t("nav.partners") },
    { to: "/#counter", label: t("nav.savings") },
    { to: "/pro", label: t("nav.for_merchants") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 bg-white ${
        scrolled ? "shadow-sm border-b border-navy-900/5" : ""
      }`}
    >
      <div className="container-app flex items-center justify-between h-16 md:h-20">
        <Logo />

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => {
            const isHash = l.to.startsWith("/#") || l.to.startsWith("#");
            const props = {
              key: l.to,
              "data-testid": `nav-${l.to.replace(/\W/g, "")}`,
              className: `text-sm font-semibold tracking-tight hover:text-navy-900 transition-colors ${
                loc.pathname === l.to ? "text-navy-900" : "text-navy-900/70"
              }`,
            };
            return isHash ? (
              <a {...props} href={l.to}>
                {l.label}
              </a>
            ) : (
              <Link {...props} to={l.to}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/admin/login" className="text-xs font-semibold text-navy-900/60 hover:text-navy-900" data-testid="admin-login-link">
            {t("nav.admin")}
          </Link>
          <a
            href={brand.referral_url || "https://www.bhealth.be"}
            target="_blank"
            rel="noreferrer"
            className="btn-lime text-sm"
            data-testid="header-cta-member"
          >
            {t("nav.become_member")}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <Sheet>
            <SheetTrigger data-testid="mobile-menu-trigger">
              <Menu className="w-6 h-6 text-navy-900" />
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] sm:w-[380px]">
              <div className="flex flex-col gap-6 mt-8">
                {navLinks.map((l) => {
                  const isHash = l.to.startsWith("/#") || l.to.startsWith("#");
                  const props = {
                    key: l.to,
                    className: "text-lg font-heading font-semibold text-navy-900",
                    "data-testid": `mobile-nav-${l.to.replace(/\W/g, "")}`,
                  };
                  return isHash ? (
                    <a {...props} href={l.to}>{l.label}</a>
                  ) : (
                    <Link {...props} to={l.to}>{l.label}</Link>
                  );
                })}
                <Link to="/admin/login" className="text-sm text-navy-900/60" data-testid="mobile-admin-login">{t("nav.admin")}</Link>
                <a href={brand.referral_url || "https://www.bhealth.be"} target="_blank" rel="noreferrer" className="btn-lime mt-4" data-testid="mobile-cta-member">{t("nav.become_member")}</a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
